import dayjs from 'dayjs';

import i18n from '@/i18n';
import {graphqlClient} from '@/services/graphqlClient';
import type {GetEventsByPairingIdForExportQuery} from '@/services/graphqlHooks';
import {useGetEventsByPairingIdForExportQuery} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import eventSubTypes from '@/translation/eventSubTypes';
import eventTypes from '@/translation/eventTypes';
import {TIME_FORMATS} from '@/utils/timeFormats';

import ResidentDateRangeOptionsFields from '../../components/export-options/ResidentDateRangeOptionsFields';
import type {ExportDefinition} from '../../types/exportTypes';
import type {ResidentDateRangeOptions} from '../exportOptions';
import {
  describeDateRange,
  formatExportDateTime,
  formatPersonName,
  getDateRangeFileName,
  getResidentDateRangeDefaultOptions,
  isWithinDateRange,
  residentDateRangeOptionsSchema,
} from '../exportOptions';

type ResidentEventExportRecord =
  GetEventsByPairingIdForExportQuery['getEventsByPairingId'][number] & {
    createdByName: string;
  };

export const residentEventsExport: ExportDefinition<
  ResidentDateRangeOptions,
  ResidentEventExportRecord
> = {
  id: 'residentEvents',
  label: i18n.t('Resident events'),
  permission: 'EXPORT_EVENTS',
  entitlement: 'EVENTS',
  optionsSchema: residentDateRangeOptionsSchema,
  OptionsFields: ResidentDateRangeOptionsFields,
  getDefaultOptions: getResidentDateRangeDefaultOptions,
  load: async options => {
    const variables = {
      input: {
        pairingId: options.residentId,
        startDate: dayjs(options.startDate)
          .subtract(1, 'day')
          .format(TIME_FORMATS.awsDate),
        endDate: dayjs(options.endDate)
          .add(1, 'day')
          .format(TIME_FORMATS.awsDate),
      },
    };
    const {getEventsByPairingId, getAllUsers} = await queryClient.fetchQuery({
      queryKey: useGetEventsByPairingIdForExportQuery.getKey(variables),
      queryFn: useGetEventsByPairingIdForExportQuery.fetcher(
        graphqlClient,
        variables
      ),
    });
    const users = new Map(getAllUsers.map(user => [user.userName, user]));

    return getEventsByPairingId
      .filter(event => isWithinDateRange(event.date, options))
      .map(event => ({
        ...event,
        createdByName:
          formatPersonName(users.get(event.createdBy)?.name) || event.createdBy,
      }))
      .sort((eventA, eventB) => dayjs(eventA.date).diff(eventB.date));
  },
  columns: [
    {header: i18n.t('Date'), value: event => formatExportDateTime(event.date)},
    {
      header: i18n.t('Type'),
      filterable: true,
      value: event => eventTypes[event.type],
    },
    {
      header: i18n.t('Sub type'),
      filterable: true,
      value: event => eventSubTypes[event.eventSubType],
    },
    {header: i18n.t('Content'), value: event => event.content},
    {
      header: i18n.t('Created by'),
      filterable: true,
      value: event => event.createdByName,
    },
  ],
  describeOptions: describeDateRange,
  getFileName: options => getDateRangeFileName('resident-events', options),
};
